"use client";

import { LoadingProductTileSkeleton } from "@faire/deprecated-retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { SDUIComponentRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIComponentRenderer/SDUIComponentRenderer";
import { SDUISectionInstanceRendererProps } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIFilterRowRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/FilterBar/SDUIFilterRowRenderer";
import { useFilterablePaginatedSectionQuery } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useFilterablePaginatedSectionQuery";
import { SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/LoadingSkeleton/SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton";
import { SDUIFilterablePaginatedSectionGridLoadingSkeleton } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/LoadingSkeleton/SDUIFilterablePaginatedSectionGridLoadingSkeleton";
import { ComponentsFlowLayoutSection } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/SectionComponentsColumn/__internal__/ComponentsFlowLayoutSection";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Column } from "@faire/web/slate/Layout";
import React from "react";

export const SDUIFilterablePaginatedSectionComponentsColumnNonRSC: React.FC<
  Omit<SDUISectionInstanceRendererProps, "section"> & {
    filterablePaginatedSection: IFilterablePaginatedSection;
    isPrimaryContent?: boolean;
  }
> = ({
  filterablePaginatedSection,
  componentRenderers,
  sectionLoggingData,
  isPrimaryContent,
  UPLTprops,
}) => {
  useTraceComponentRender(
    "SDUIFilterablePaginatedSectionComponentsColumnNonRSC"
  );
  const {
    isFilterLoading,
    isFilteredComponentsFetching,
    filteredComponentsData,
    isFilteredComponentsError,
  } = useFilterablePaginatedSectionQuery(filterablePaginatedSection);

  const filteredComponents = filteredComponentsData?.components;
  const filterBarComponent = filteredComponents?.find(
    (component) => component.filter_bar_component
  );

  if (isFilteredComponentsFetching || isFilteredComponentsError) {
    return (
      <Column style={{ width: "100%", minWidth: 0 }}>
        {filterBarComponent ? (
          <SDUIFilterRowRenderer
            key="sdui_filter_row_component_no_grid"
            component={filterBarComponent}
            sectionLoggingParams={sectionLoggingData}
            isFilterLoading={isFilterLoading}
          />
        ) : (
          <SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton />
        )}
        <SDUIFilterablePaginatedSectionGridLoadingSkeleton
          loadingSkeletonNode={<LoadingProductTileSkeleton />}
          numTilesPerRowResponsive={{
            mobileAndAbove: 2,
            tabletAndAbove: 4,
            desktopAndAbove: 4,
            xLargeAndAbove: 5,
          }}
        />
      </Column>
    );
  }

  if (filteredComponentsData?.layout) {
    return (
      <Column style={{ width: "100%", minWidth: 0 }}>
        <ComponentsFlowLayoutSection
          filteredComponents={filteredComponentsData}
          componentRenderers={componentRenderers}
          sectionLoggingData={sectionLoggingData}
          isPrimaryContent={isPrimaryContent}
          UPLTprops={UPLTprops}
        />
      </Column>
    );
  }

  return (
    <Column style={{ width: "100%", minWidth: 0 }}>
      {filterBarComponent ? (
        <SDUIFilterRowRenderer
          key="sdui_filter_row_component"
          component={filterBarComponent}
          sectionLoggingParams={sectionLoggingData}
          isFilterLoading={isFilterLoading}
        />
      ) : (
        <SDUIFilterablePaginatedSectionFilterRowLoadingSkeleton />
      )}

      {filteredComponents
        ?.filter((component) => !component.filter_bar_component)
        .map((component) => (
          <SDUIComponentRenderer
            key={component.id}
            component={component}
            componentRenderers={componentRenderers}
            sectionLoggingParams={sectionLoggingData}
            isPrimaryContent={!!component.is_required_for_interactivity}
            renderBehaviourOverrides={{
              gridOptimizations: true,
            }}
          />
        ))}
    </Column>
  );
};
